import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { tokenStorage } from "@/auth/tokenStorage";
import { openSseStream } from "@/lib/sseClient";

import type { HelpScreen } from "./useHelpContext";

export interface HelpMessage {
  role: "user" | "assistant";
  content: string;
}

/**
 * Estado do chat de ajuda (efemero, so no cliente). O backend e stateless:
 * reenviamos o historico inteiro a cada turno. ``reset`` limpa tudo
 * ("novo chat"). Streaming via SSE reaproveitando ``openSseStream``.
 */
export function useHelpChat(screen: HelpScreen) {
  const { t } = useTranslation("help");
  const [messages, setMessages] = useState<HelpMessage[]>([]);
  const [streaming, setStreaming] = useState(false);

  // Refs para ler valores atuais dentro do callback sem recriar o stream.
  const messagesRef = useRef<HelpMessage[]>([]);
  messagesRef.current = messages;
  const screenRef = useRef(screen);
  screenRef.current = screen;
  const abortRef = useRef<AbortController | null>(null);

  const finish = useCallback(() => {
    setStreaming(false);
    abortRef.current = null;
    // Remove a bolha do assistente se ficou vazia (erro/sem conteudo).
    setMessages((prev) => {
      const last = prev[prev.length - 1];
      if (last && last.role === "assistant" && last.content === "") {
        return prev.slice(0, -1);
      }
      return prev;
    });
  }, []);

  const send = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || streaming) return;
      setStreaming(true);

      const outgoing: HelpMessage[] = [
        ...messagesRef.current,
        { role: "user", content: trimmed },
      ];
      // Otimista: mensagem do usuario + bolha vazia do assistente (streaming).
      setMessages([...outgoing, { role: "assistant", content: "" }]);

      void (async () => {
        const token = await tokenStorage.getToken();
        abortRef.current = openSseStream(
          {
            path: "/help/chat",
            method: "POST",
            body: {
              messages: outgoing,
              screen: {
                path: screenRef.current.path,
                label: screenRef.current.label,
              },
            },
          },
          {
            token,
            onEvent: (ev) => {
              const data = (ev.data ?? {}) as Record<string, unknown>;
              if (ev.event === "token") {
                const chunk = String(data.content ?? "");
                if (!chunk) return;
                setMessages((prev) => {
                  const next = [...prev];
                  const last = next[next.length - 1];
                  if (last && last.role === "assistant") {
                    next[next.length - 1] = {
                      ...last,
                      content: last.content + chunk,
                    };
                  }
                  return next;
                });
              } else if (ev.event === "error") {
                toast.error(String(data.message ?? t("error")));
              } else if (ev.event === "done") {
                finish();
              }
            },
            onClose: finish,
            onError: (err) => {
              console.error("Help SSE error", err);
              toast.error(t("error"));
              finish();
            },
          },
        );
      })();
    },
    [streaming, finish, t],
  );

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStreaming(false);
    setMessages([]);
  }, []);

  return { messages, streaming, send, reset };
}
